import { Button } from '@heidi/design-system/button';
import { Checkbox } from '@heidi/design-system/checkbox';
import { Label } from '@heidi/design-system/label';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import type { ReactNode } from 'react';
import { FormattedMessage, type MessageDescriptor } from 'react-intl';

export interface LibraryRiskBullet {
  icon: ReactNode;
  message: MessageDescriptor;
}

export interface LibraryRiskOptinProps {
  /** Intro paragraph shown above the risk bullets. */
  intro: MessageDescriptor;
  /** One row per risk, each with a leading icon. */
  bullets: LibraryRiskBullet[];
  /** Label for the mandatory acceptance checkbox. */
  checkboxLabel: MessageDescriptor;
  /** Primary (confirm) button label. */
  confirmLabel: MessageDescriptor;
  /** Optional cancel button label; only rendered when `onCancel` is provided. */
  cancelLabel?: MessageDescriptor;
  /** Unique id wiring the checkbox to its label (multiple opt-ins can coexist). */
  checkboxId: string;
  /** Current checkbox state, owned by the parent so it can reset on reopen. */
  accepted: boolean;
  onAcceptedChange: (accepted: boolean) => void;
  /** Confirms the opt-in. Only invokable once the checkbox is ticked. */
  onConfirm: () => void;
  /** Optional dismiss action; when omitted the cancel button is hidden. */
  onCancel?: () => void;
  /** Optional test id for the confirm button. */
  confirmTestId?: string;
}

/**
 * Presentational, reusable risk opt-in step for the Evidence library
 * (EVI-1566, EV-020). Rendered inline as the first step *inside* an existing
 * surface (the Upload Documents modal, or a New Collection tab) rather than a
 * separate stacked dialog, and only when the user has not previously accepted.
 * The confirm button stays disabled until the checkbox is ticked — an explicit
 * opt-in, not passive consent.
 */
export const LibraryRiskOptin = ({
  intro,
  bullets,
  checkboxLabel,
  confirmLabel,
  cancelLabel,
  checkboxId,
  accepted,
  onAcceptedChange,
  onConfirm,
  onCancel,
  confirmTestId,
}: LibraryRiskOptinProps) => (
  <div className="flex flex-col gap-4">
    <TypographyP2 className="text-text-secondary">
      <FormattedMessage {...intro} />
    </TypographyP2>

    <div className="flex flex-col gap-3">
      {bullets.map((bullet, index) => (
        <div
          key={index}
          className="flex items-start gap-3 rounded-lg bg-background-secondary p-3"
        >
          {bullet.icon}
          <TypographyP2 className="text-text-primary">
            <FormattedMessage {...bullet.message} />
          </TypographyP2>
        </div>
      ))}
    </div>

    <div className="flex items-center gap-2">
      <Checkbox
        id={checkboxId}
        checked={accepted}
        onCheckedChange={(checked) => onAcceptedChange(checked === true)}
      />
      <Label
        htmlFor={checkboxId}
        className="cursor-pointer leading-none text-text-primary"
      >
        <TypographyCaption className="text-text-primary">
          <FormattedMessage {...checkboxLabel} />
        </TypographyCaption>
      </Label>
    </div>

    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
      {onCancel && cancelLabel && (
        <Button size="lg" variant="ghost" onClick={onCancel}>
          <FormattedMessage {...cancelLabel} />
        </Button>
      )}
      <Button
        size="lg"
        data-testid={confirmTestId}
        disabled={!accepted}
        onClick={onConfirm}
      >
        <FormattedMessage {...confirmLabel} />
      </Button>
    </div>
  </div>
);
